/* eslint-disable consistent-return */
/* eslint-disable default-case */
/* eslint-disable no-underscore-dangle */
import { z } from 'zod';
import {
  badgeSchema, getBadge, SubType, convertToBadge,
} from 'lib/types/article';

/*
 * .strict() is used on the following types because this should help us keep these
 * schemas in sync with the GraphQL query.  It should also help ensure that we
 * don't overfetch.
 */

const Badge = z
  .object({
    name: z.string(),
  })
  .strict();

const Headline = z
  .object({
    tease: z.string(),
  })
  .strict();

const Url = z
  .object({
    primary: z.string(),
  })
  .strict();

const BadgeTaxonomy = z
  .object({
    badge: Badge.nullable(),
  })
  .strict()
  .nullable();

const Video = z
  .object({
    __typename: z.literal('Video'),
    headline: Headline,
    url: Url,
    taxonomy: BadgeTaxonomy,
  })
  .strict();

const Card = z
  .object({
    __typename: z.literal('Card'),
    headline: Headline,
    url: Url,
  })
  .strict();

const ExternalLink = z
  .object({
    __typename: z.literal('ExternalLink'),
    text: z.string(),
    url: Url,
  })
  .strict();

const ArticleCard = z
  .object({
    date: z.object({
      publishedAt: z.string().nullable(),
    }),
  })
  .strict();

const Article = z
  .object({
    __typename: z.literal('Article'),
    subType: SubType,
    date: z
      .object({
        lastPublishedAt: z.string().nullable(),
      })
      .strict(),
    eventDateTime: z
      .object({
        endsAt: z.string().nullable(),
      })
      .strict(),
    flag: z.string().nullable(),
    cards: z
      .object({
        items: z.array(ArticleCard.nullable()).nullable(),
      })
      .nullable(),
    breakingNews: z.boolean().nullable(),
    headline: Headline,
    url: Url,
    taxonomy: BadgeTaxonomy,
  })
  .strict();

const subnavReponseItemSchema = z.union([Video, Card, ExternalLink, Article]);

/**
 * A subnav item as it appears in the form of the Bento API response.
 *
 * @typedef {z.output<typeof subnavReponseItemSchema>} SubnavResponseItem
 */
export const subnavResponseItemsSchema = z.array(subnavReponseItemSchema).optional();

/** @typedef {z.output<typeof badgeSchema>} Badge */
const subnavItemSchema = z.object({
  text: z.string(),
  url: z.string(),
  badge: badgeSchema,
});

export const subnavItemsSchema = z.array(subnavItemSchema);

/**
 * A canonical subnav item.  It just contains the basics that a presentation
 * component with no knowledge of business logic would want.
 *
 * @typedef {z.output<typeof subnavItemSchema>} SubnavItem
 */
/** @typedef {z.output<typeof subnavItemsSchema>} SubnavItems */

/**
 * Converts a subnav item as it appears in the form of an API response, and turns
 * it into a canonical form, applying any necessary logic.
 *
 * @param {SubnavResponseItem} item
 * @returns {SubnavItem}
 */
function convertToStandardSubnavItem(item) {
  switch (item.__typename) {
    case 'Video':
      return {
        text: item.headline.tease,
        url: item.url.primary,
        badge: convertToBadge(item.taxonomy?.badge?.name),
      };

    case 'Card':
      return {
        text: item.headline.tease,
        url: item.url.primary,
      };

    case 'ExternalLink':
      return {
        text: item.text,
        url: item.url.primary,
      };

    case 'Article': {
      const badge = getBadge(item);

      return {
        text: item.headline.tease,
        url: item.url.primary,
        badge,
      };
    }
  }
}

/**
 * Converts the API response into a canonical array of subnav items
 *
 * @param {SubnavResponseItem[]} input - the `items` from a subnav query.
 * @returns {SubnavItems} - SubnavItems in a standard format
 */
export function getStandardSubnav(input) {
  return input.map(convertToStandardSubnavItem);
}
